import React from 'react';
import {
  Box,
  Panel,
  Columns,
  Column,
  Tag,
  LabelValue,
  Skeleton,
  Layout,
  Tooltip
} from 'src/components/matchbox';
import { TranslatableText } from 'src/components/text';
import { formatDate } from '@sparkpost/report-builder/helpers/date';
import { snakeToFriendly } from 'src/helpers/string';
import { StyledQuote } from './styles';
import { ActiveFilters, LabelValueLoadingSkeleton } from 'src/components';
import { InfoOutline } from '@sparkpost/matchbox-icons';
import { tokens } from '@sparkpost/design-tokens';

export default function AlertDetails({ alert, loading, showDetailsSection }) {
  const columnsLength = showDetailsSection ? 4 : 5;

  if (loading) {
    return (
      <Layout.Section>
        <Panel>
          {!showDetailsSection && <Panel.SubHeader>Details</Panel.SubHeader>}
          <Panel.Section>
            <LabelValueLoadingSkeleton
              labelValues={[
                { value: 'Alert Name', width: 1 / 6 },
                { value: 'Date Created', width: 1 / 6 },
                { value: 'Last Incident', width: 1 / 6 },
                {
                  value: (
                    <Tooltip
                      content="Severity Level is a user-designated label to categorize the urgency for which an alert should be prioritized. It does not impact how the alert performs."
                      id="severity-level-tooltip"
                    >
                      Severity Level
                    </Tooltip>
                  ),
                  width: 1 / 6
                },
                { value: 'Notifications', width: 1 / 6 }
              ]}
            />
          </Panel.Section>
          <Panel.SubHeader>Alert Summary</Panel.SubHeader>
          <Panel.Section>
            <Skeleton.Body />
          </Panel.Section>
        </Panel>
      </Layout.Section>
    );
  }
  return (
    <Layout.Section>
      <Panel>
        {!showDetailsSection && <Panel.SubHeader>Details</Panel.SubHeader>}
        <Panel.Section>
          <Columns>
            <Column width={1 / columnsLength}>
              <LabelValue>
                <LabelValue.Label>Alert Name</LabelValue.Label>
                <LabelValue.Value>{alert.name}</LabelValue.Value>
              </LabelValue>
            </Column>
            <Column width={1 / columnsLength}>
              <LabelValue.Label>Date Created</LabelValue.Label>
              <LabelValue.Value>{formatDate(alert.first_incident_fired)}</LabelValue.Value>
            </Column>
            {showDetailsSection && (
              <Column width={1 / columnsLength}>
                <LabelValue.Label>Subaccount Assignment</LabelValue.Label>
                <LabelValue.Value>
                  {alert.assignment === 'primary' ? 'Primary Account' : alert.assignment}
                </LabelValue.Value>
              </Column>
            )}
            {!showDetailsSection && (
              <Column width={1 / columnsLength}>
                <LabelValue.Label>Last Incident</LabelValue.Label>
                <LabelValue.Value>{formatDate(alert.last_incident_fired)}</LabelValue.Value>
              </Column>
            )}
            <Column width={1 / columnsLength}>
              <Tooltip
                content="Severity Level is a user-designated label to categorize the urgency for which an alert should be prioritized. It does not impact how the alert performs."
                id="severity-level-tooltip"
              >
                <LabelValue.Label>
                  Severity Level <InfoOutline fill={tokens.color_blue_700} />
                </LabelValue.Label>
              </Tooltip>
              <LabelValue.Value>
                <Tag color="red">{alert.severity}</Tag>
              </LabelValue.Value>
            </Column>
            {!showDetailsSection && (
              <Column width={1 / columnsLength}>
                <LabelValue.Label>Notifications</LabelValue.Label>
                <Box>
                  <Tag>{alert.notifications}</Tag>
                </Box>
              </Column>
            )}
          </Columns>
          {showDetailsSection && (
            <Box mt="500">
              <LabelValue.Label>Alert Name</LabelValue.Label>
              <LabelValue.Value>
                <Box>
                  <TranslatableText>The alert is triggered when </TranslatableText>
                  <Tag color="blue">{snakeToFriendly(alert.metric)}</Tag>
                  <TranslatableText> is </TranslatableText>
                  <Tag color="blue">{alert.condition}</Tag>{' '}
                  <Tag color="blue">{alert.threshold_evaluator.value}%</Tag>
                  <TranslatableText> at the end of a </TranslatableText>
                  <Tag color="blue">1 {alert.precision}</Tag> period.
                </Box>
                <Box mt="300">
                  <StyledQuote>
                    <ActiveFilters filters={alert.filters} />
                  </StyledQuote>
                </Box>
                <TranslatableText> and is broken down by </TranslatableText>
                <Tag color="blue">{alert.assignment}</Tag>.
              </LabelValue.Value>
            </Box>
          )}
        </Panel.Section>
        {!showDetailsSection && (
          <Panel.Section>
            <Box>
              <TranslatableText>The alert is triggered when </TranslatableText>
              <Tag color="blue">{snakeToFriendly(alert.metric)}</Tag>
              <TranslatableText> is </TranslatableText>
              <Tag color="blue">{alert.condition}</Tag>{' '}
              <Tag color="blue">{alert.threshold_evaluator.value}%</Tag>
              <TranslatableText> at the end of a </TranslatableText>
              <Tag color="blue">1 {alert.precision}</Tag> period.
            </Box>
            <Box mt="300">
              <StyledQuote>
                <ActiveFilters filters={alert.filters} />
              </StyledQuote>
            </Box>
            <TranslatableText> and is broken down by </TranslatableText>
            <Tag color="blue">{alert.assignment}</Tag>.
          </Panel.Section>
        )}
      </Panel>
    </Layout.Section>
  );
}
